import { useMemo } from 'react';
import NextImage from 'next/image';
import classNames from 'classnames';
import { Drawer, Image, List, Tabs, Text } from '@mantine/core';
import { useSuggestedSudsContext } from '@/contexts/SuggestedSudsContext';
import { PerformanceStat } from './PerformanceStat';
import { SUDS_METADATA } from './SudsMetadata';
import styles from './drawer.module.css';

type DetailProps = {
  title: string;
  value: string | undefined;
};

const Detail = ({ title, value }: DetailProps) => {
  return (
    <div className={styles.textGroup}>
      <Text className={styles.titleText}>{title}</Text>
      <Text className={styles.regularText}>{value ?? '-'}</Text>
    </div>
  );
};

type DetailsProps = {
  title: string;
  values: string[];
};

const Details = ({ title, values }: DetailsProps) => {
  return (
    <div className={styles.list}>
      <Text className={styles.titleText}>{title}</Text>
      <List>
        {values.map((value, index) => (
          <List.Item key={`list-item-${index}`}>
            <Text className={styles.regularText}>{value}</Text>
          </List.Item>
        ))}
      </List>
    </div>
  );
};

export type SuggestedSudsProperties = {
  fid: number;
  area: number;
  dist_to_csomean: number;
  Reduced_runoff: number;
  Increased_wate_retention: number;
  'SuDS Type': string;
};

export const SuggestedSudsDrawer = () => {
  const {
    isSuggestedSudsDrawerOpen,
    suggestedSudProperties: properties,
    closeSuggestedSudsDrawer,
  } = useSuggestedSudsContext();

  const extraDetails = useMemo(() => {
    if (properties && properties['SuDS Type']) {
      const type = properties['SuDS Type'];
      if (['Green roof', 'Swale', 'Detention basin', 'Rain garden'].includes(type)) {
        return SUDS_METADATA[type as keyof typeof SUDS_METADATA];
      }
    }
    return null;
  }, [properties]);

  return (
    <Drawer
      opened={isSuggestedSudsDrawerOpen}
      onClose={closeSuggestedSudsDrawer}
      title={extraDetails?.displayName ?? ''}
      withinPortal={false}
      withOverlay={false}
      position="left"
      classNames={{
        root: styles.root,
        inner: styles.inner,
        body: styles.body,
        content: styles.root,
      }}
    >
      {properties && (
        <>
          <hr className={styles.divider} />
          <Tabs defaultValue="details" mt={0}>
            <Tabs.List grow>
              <Tabs.Tab pt={12} pb={12} pr="xs" pl="xs" value="details">
                <Text size="xs">Details</Text>
              </Tabs.Tab>
              <Tabs.Tab pt={12} pb={12} pr="xs" pl="xs" value="performance">
                <Text size="xs">Performance</Text>
              </Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel className={classNames(styles.panel, styles.details)} value="details">
              <Detail title="SuD Type" value={extraDetails?.displayName} />
              <Detail title="SuD Location Area" value={`${properties.area.toFixed(1)} m²`} />
              <Detail
                title="Distance to CSO"
                value={`${properties.dist_to_csomean.toFixed(1)} m`}
              />
              {extraDetails && (
                <>
                  <Detail title="Primary Function" value={extraDetails.primaryFunction} />
                  <Detail title="Approximate Cost" value={`${extraDetails.approxCost} per m²`} />
                  <hr className={styles.divider} />
                  <Image
                    component={NextImage}
                    src={extraDetails.img}
                    alt="SuDS Image"
                    h={100}
                    w="auto"
                    fit="contain"
                  />
                  <Details title="Design" values={extraDetails.design} />
                  <Details title="Benefits" values={extraDetails.benefits} />
                  <Details title="Limitations" values={extraDetails.limitations} />
                </>
              )}
            </Tabs.Panel>

            <Tabs.Panel className={styles.panel} value="performance">
              <div className={styles.stats}>
                <Text fw={600} size="xs">
                  Impact Summary
                </Text>
                <PerformanceStat
                  key="runoff-stat"
                  label="REDUCED RUNOFF"
                  value={properties.Reduced_runoff.toFixed(2)}
                />
                <PerformanceStat
                  key="retention-stat"
                  label="INCREASED WATER RETENTION"
                  value={properties.Increased_wate_retention.toFixed(2)}
                />
              </div>
            </Tabs.Panel>
          </Tabs>
        </>
      )}
    </Drawer>
  );
};
